/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart3, TrendingUp, TrendingDown, Euro, Star, ClipboardList, AlertTriangle, Activity } from 'lucide-react';
import { getDoctorAnalytics } from '@/lib/sprint5';
import RxStatsChart from '@/components/analytics/RxStatsChart';

const KPI = ({ Icon, label, value, delta, sub }) => (
  <Card data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-2 text-2xl font-bold font-display">{value}</div>
      {(delta !== undefined && delta !== null) && (
        <div className={`text-[11px] font-semibold flex items-center gap-1 ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {delta >= 0 ? '+' : ''}{delta}% vs mese precedente
        </div>
      )}
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

const DoctorAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await getDoctorAnalytics()); } catch (e) { /* auth */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary" /> Analytics
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Ultimi 30 giorni. Confronto con i 30 giorni precedenti.</p>
      </div>

      {!data ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[1,2,3,4].map((i) => <Skeleton key={i} className="h-28" />)}</div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KPI Icon={Euro} label="Ricavi" value={`€ ${data.revenue.current.toLocaleString('it')}`} delta={data.revenue.delta_pct} />
            <KPI Icon={ClipboardList} label="Appuntamenti" value={data.appointments.total} sub={`${data.appointments.completed} completati · ${data.appointments.upcoming_or_confirmed} in agenda`} />
            <KPI Icon={AlertTriangle} label="No-show" value={`${data.no_show_rate_pct}%`} sub={`${data.appointments.no_show} pazienti`} />
            <KPI Icon={Activity} label="Occupancy" value={`${data.occupancy_rate_pct}%`} sub="su 60 slot potenziali" />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Star className="h-3.5 w-3.5" /> Rating medio</div>
                <div className="text-3xl font-bold font-display flex items-baseline gap-2">
                  {data.rating.avg ?? '—'} <span className="text-sm text-muted-foreground">/ 5</span>
                </div>
                <div className="text-[11px] text-muted-foreground">{data.rating.count} recensioni</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">Top 5 ICD-10 diagnosi</div>
                {data.top_icd10.length === 0 ? <div className="text-sm text-muted-foreground">Nessuna diagnosi ancora codificata.</div> : (
                  <ul className="space-y-1">
                    {data.top_icd10.map((r) => (
                      <li key={r.code} className="flex items-center justify-between text-sm">
                        <Badge variant="outline">{r.code}</Badge>
                        <span className="text-xs text-muted-foreground">{r.count} casi</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-semibold mb-3">Andamento giornaliero</div>
              {data.daily.length === 0 ? (
                <div className="text-sm text-muted-foreground">Nessuna visita negli ultimi 30 giorni.</div>
              ) : (
                <div className="space-y-1" data-testid="analytics-daily-chart">
                  {data.daily.map((d) => (
                    <div key={d.date} className="flex items-center gap-2 text-xs">
                      <span className="w-20 font-mono text-muted-foreground">{d.date}</span>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${Math.min((d.count / 5) * 100, 100)}%` }} />
                      </div>
                      <span className="w-16 text-right">{d.count} visite</span>
                      <span className="w-20 text-right text-muted-foreground">€ {d.revenue.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <RxStatsChart days={30} limit={10} />
        </>
      )}
    </div>
  );
};

export default DoctorAnalyticsPage;
